// Background music for My Story result page.
//
// Strategy:
// 1. If real MP3 files are available under /audio/story/track1.mp3 … track5.mp3,
//    play one at random in a loop at low volume.
// 2. Otherwise, synthesize a cheerful Indian pentatonic melody (raga Bhupali =
//    Sa Re Ga Pa Dha = C D E G A) via WebAudio. Picks one of 4 pre-composed
//    melodies at random each loop, so it doesn't feel repetitive.
//
// All audio is generated client-side. No external dependencies.

const NOTE = {
  C3: 130.81, D3: 146.83, E3: 164.81, G3: 196.0, A3: 220.0,
  C4: 261.63, D4: 293.66, E4: 329.63, G4: 392.0, A4: 440.0,
  C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880.0,
};

// Each entry: array of [noteName, durationInBeats].
// Tuned to feel like a light bansuri-led cartoon theme.
// 10 melodies in Indian pentatonic / cinematic moods so each story session can
// pick a fresh tune. We avoid repeating the previous melody by remembering the
// last-played index in localStorage.
const MELODIES = [
  // 1. Skipping & playful — raga Bhupali
  [
    ["C5", 0.5], ["E5", 0.5], ["G5", 1], ["E5", 0.5], ["D5", 0.5], ["C5", 1],
    ["A4", 0.5], ["C5", 0.5], ["E5", 1], ["G5", 0.5], ["E5", 0.5], ["C5", 1],
    ["D5", 0.5], ["E5", 0.5], ["G5", 1], ["A5", 0.5], ["G5", 0.5], ["E5", 1],
    ["C5", 1], ["D5", 1], ["E5", 1], ["C5", 1],
  ],
  // 2. Lullaby-like
  [
    ["E5", 1], ["D5", 1], ["C5", 2], ["G4", 1], ["A4", 1], ["C5", 2],
    ["E5", 1], ["G5", 1], ["E5", 2], ["D5", 1], ["C5", 1], ["A4", 2],
    ["G4", 1], ["C5", 1], ["E5", 2], ["C5", 2],
  ],
  // 3. Joyful staccato
  [
    ["G4", 0.5], ["A4", 0.5], ["C5", 0.5], ["E5", 0.5],
    ["G5", 1], ["E5", 0.5], ["C5", 0.5], ["G4", 1],
    ["A4", 0.5], ["C5", 0.5], ["D5", 0.5], ["E5", 0.5],
    ["G5", 1], ["D5", 0.5], ["C5", 0.5], ["A4", 1],
    ["C5", 0.5], ["E5", 0.5], ["G5", 0.5], ["C5", 0.5],
    ["G4", 0.5], ["E4", 0.5], ["C4", 1],
  ],
  // 4. Ascending–descending raga Bhupali (ārohaṇa-avarohaṇa)
  [
    ["C4", 1], ["D4", 1], ["E4", 1], ["G4", 1], ["A4", 1], ["C5", 1],
    ["A4", 1], ["G4", 1], ["E4", 1], ["D4", 1], ["C4", 2],
    ["G4", 1], ["A4", 1], ["G4", 1], ["E4", 1], ["C4", 2],
  ],
  // 5. Floating folk waltz (3/4 feel)
  [
    ["E5", 1.5], ["D5", 0.5], ["C5", 1], ["G4", 1.5], ["A4", 0.5], ["C5", 1],
    ["E5", 1.5], ["G5", 0.5], ["E5", 1], ["D5", 1.5], ["C5", 0.5], ["A4", 1],
    ["G4", 1], ["A4", 1], ["C5", 2], ["G4", 1], ["E4", 1], ["C4", 2],
  ],
  // 6. Skipping puppet
  [
    ["C5", 0.25], ["E5", 0.25], ["G5", 0.5], ["E5", 0.25], ["C5", 0.25], ["A4", 0.5],
    ["G4", 0.25], ["A4", 0.25], ["C5", 0.5], ["E5", 0.25], ["G5", 0.25], ["E5", 0.5],
    ["D5", 0.25], ["E5", 0.25], ["G5", 0.5], ["A5", 0.25], ["G5", 0.25], ["E5", 0.5],
    ["D5", 0.25], ["C5", 0.25], ["A4", 0.5], ["C5", 0.5], ["D5", 0.5], ["E5", 1],
    ["G5", 0.5], ["E5", 0.5], ["C5", 1],
  ],
  // 7. Calm river — raga Megh-tinged
  [
    ["G4", 2], ["A4", 1], ["C5", 1], ["D5", 2], ["E5", 2],
    ["G5", 1], ["E5", 1], ["D5", 1], ["C5", 1], ["A4", 2],
    ["C5", 1], ["D5", 1], ["C5", 1], ["A4", 1], ["G4", 2],
  ],
  // 8. Treetop chase
  [
    ["A4", 0.5], ["C5", 0.5], ["E5", 0.5], ["G5", 0.5],
    ["A5", 1], ["G5", 0.5], ["E5", 0.5], ["C5", 0.5], ["A4", 0.5], ["G4", 1],
    ["C5", 0.5], ["E5", 0.5], ["G5", 0.5], ["C5", 0.5],
    ["E5", 1], ["D5", 0.5], ["C5", 0.5], ["G4", 0.5], ["E4", 0.5], ["C4", 1],
  ],
  // 9. Festive ladoo — call & response
  [
    ["G4", 0.5], ["C5", 0.5], ["E5", 1],
    ["E5", 0.5], ["D5", 0.5], ["C5", 1],
    ["A4", 0.5], ["D5", 0.5], ["G5", 1],
    ["G5", 0.5], ["E5", 0.5], ["C5", 1],
    ["C5", 0.5], ["D5", 0.5], ["E5", 0.5], ["G5", 0.5],
    ["A5", 1], ["G5", 0.5], ["E5", 0.5], ["C5", 2],
  ],
  // 10. Stars at night — raga Bhupali, slow & dreamy
  [
    ["C5", 2], ["E5", 2], ["G5", 2], ["A5", 2],
    ["G5", 1], ["E5", 1], ["D5", 2], ["C5", 2],
    ["A4", 2], ["G4", 2], ["C5", 4],
  ],
];

const BPM = 88;
// Drop real MP3 files into /app/frontend/public/audio/story/ to override the
// procedural synth. The player will use a different track each time the page
// loads (round-robin within the available pool).
const MP3_CANDIDATES = [
  "/audio/story/track1.mp3",
  "/audio/story/track2.mp3",
  "/audio/story/track3.mp3",
  "/audio/story/track4.mp3",
  "/audio/story/track5.mp3",
  "/audio/story/track6.mp3",
  "/audio/story/track7.mp3",
  "/audio/story/track8.mp3",
];
const MAX_LOOP_SEC = 30;

// --- "Pick a different track than last time" helper -----------------
// Stores the last-used melody index per "kind" ("synth" or "mp3") so two
// consecutive story generations never share the same background tune.
function pickFreshIndex(kind, total) {
  if (total <= 1) return 0;
  const key = `dt_story_music_last_${kind}`;
  let last = -1;
  try {
    const v = parseInt(localStorage.getItem(key) || "-1", 10);
    if (Number.isFinite(v)) last = v;
  } catch (_) { /* private mode */ }
  let next;
  do {
    next = Math.floor(Math.random() * total);
  } while (next === last && total > 1);
  try { localStorage.setItem(key, String(next)); } catch (_) { /* ignore */ }
  return next;
}

async function detectAvailableMp3s() {
  const checks = await Promise.all(
    MP3_CANDIDATES.map(async (url) => {
      try {
        const r = await fetch(url, { method: "HEAD", cache: "no-store" });
        return r.ok ? url : null;
      } catch (_) { return null; }
    })
  );
  return checks.filter(Boolean);
}

export function createStoryMusicPlayer({ volume = 0.18 } = {}) {
  // Singleton enforcement — only ONE story-music player can be active
  // app-wide at any moment. If a previous player still exists (e.g. React
  // StrictMode dev double-effect, rapid nav, or a stale mount), tear it
  // down BEFORE creating a new one so we can never overlap two loops.
  try { if (window.__storyMusicPlayer) window.__storyMusicPlayer.stop(); } catch (_) { /* ignore */ }

  let ac = null;
  let masterGain = null;
  let stopped = true;
  let timer = null;
  let htmlAudio = null;
  // Lock the chosen melody for this player instance so subsequent loop cycles
  // use the same tune. A new story generation creates a new player, which
  // picks a fresh index (different from the previous run).
  const melodyIdx = pickFreshIndex("synth", MELODIES.length);

  const ensureContext = () => {
    if (!ac) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ac = new AC();
    }
    if (!masterGain) {
      masterGain = ac.createGain();
      masterGain.gain.value = volume;
      masterGain.connect(ac.destination);
    }
    return ac;
  };

  const playNote = (freq, when, dur, gainPeak) => {
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "triangle";
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(gainPeak, when + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    o.connect(g).connect(masterGain);
    o.start(when);
    o.stop(when + dur + 0.02);

    // Subtle harmonic layer — gives bansuri-like warmth
    const o2 = ac.createOscillator();
    const g2 = ac.createGain();
    o2.type = "sine";
    o2.frequency.value = freq * 2;
    g2.gain.setValueAtTime(0.0001, when);
    g2.gain.exponentialRampToValueAtTime(gainPeak * 0.18, when + 0.05);
    g2.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    o2.connect(g2).connect(masterGain);
    o2.start(when);
    o2.stop(when + dur + 0.02);
  };

  const playBass = (freq, when, dur) => {
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "sine";
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(0.22, when + 0.1);
    g.gain.linearRampToValueAtTime(0.16, when + dur * 0.7);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    o.connect(g).connect(masterGain);
    o.start(when);
    o.stop(when + dur + 0.02);
  };

  // Soft tabla-like tap on beat 1 of every bar
  const playTabla = (when) => {
    const buf = ac.createBuffer(1, ac.sampleRate * 0.18, ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / ac.sampleRate;
      data[i] = (Math.random() * 2 - 1) * Math.exp(-t * 28) * 0.6;
    }
    const src = ac.createBufferSource();
    src.buffer = buf;
    const g = ac.createGain();
    g.gain.value = 0.45;
    const filt = ac.createBiquadFilter();
    filt.type = "lowpass";
    filt.frequency.value = 480;
    src.connect(filt).connect(g).connect(masterGain);
    src.start(when);
  };

  const playMelodyCycle = () => {
    if (stopped) return;
    const beat = 60 / BPM;
    const melody = MELODIES[melodyIdx];
    let t = ac.currentTime + 0.05;
    const totalBeats = melody.reduce((s, [, b]) => s + b, 0);
    const totalSec = Math.min(MAX_LOOP_SEC, totalBeats * beat);

    // Drone bass — root + fifth
    playBass(NOTE.C3, t, totalSec);
    playBass(NOTE.G3, t + totalSec * 0.5, totalSec * 0.5);

    // Tabla taps every 2 beats
    for (let b = 0; b < totalBeats; b += 2) playTabla(t + b * beat);

    // Melody
    for (const [n, b] of melody) {
      const dur = b * beat;
      playNote(NOTE[n], t, Math.min(dur * 0.95, dur), 0.4);
      t += dur;
    }
    timer = setTimeout(playMelodyCycle, Math.max(0, totalSec * 1000 - 150));
  };

  const player = {
    play: async () => {
      // Kick off the synth loop *immediately* so the user hears music the
      // moment the GIF appears — no waiting on the MP3 HEAD probes below.
      // If real MP3 files turn out to be available, we swap over to them
      // and pause the synth.
      const c = ensureContext();
      if (c) {
        if (c.state === "suspended") {
          try { await c.resume(); } catch (_) { /* user gesture needed */ }
        }
        stopped = false;
        playMelodyCycle();
      }

      // MP3 upgrade path — runs in parallel; only takes over if we find
      // a real file. Still fully cancellable via stop().
      try {
        const mp3s = await detectAvailableMp3s();
        if (stopped) return; // stop() was called while we were probing
        if (mp3s.length > 0) {
          const idx = pickFreshIndex("mp3", mp3s.length);
          const url = mp3s[idx];
          const upgraded = new Audio(url);
          upgraded.loop = true;
          upgraded.volume = Math.max(0.05, Math.min(1, volume * 1.5));
          try {
            await upgraded.play();
          } catch (_) {
            // Autoplay blocked — keep the synth going instead of leaving
            // the user in silence.
            return;
          }
          if (stopped) { try { upgraded.pause(); } catch (_) { /* ignore */ } return; }
          // Pause the synth in favor of the real MP3.
          if (timer) { clearTimeout(timer); timer = null; }
          if (masterGain && ac) {
            try { masterGain.gain.setValueAtTime(0.0001, ac.currentTime); } catch (_) { /* ignore */ }
          }
          htmlAudio = upgraded;
        }
      } catch (_) { /* keep synth running */ }
    },
    stop: () => {
      stopped = true;
      if (timer) { clearTimeout(timer); timer = null; }
      if (htmlAudio) {
        try { htmlAudio.pause(); htmlAudio.src = ""; } catch (_) { /* ignore */ }
        htmlAudio = null;
      }
      if (masterGain && ac) {
        try {
          const now = ac.currentTime;
          masterGain.gain.cancelScheduledValues(now);
          masterGain.gain.setValueAtTime(masterGain.gain.value, now);
          masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
        } catch (_) { /* ignore */ }
      }
      setTimeout(() => {
        if (ac) { try { ac.close(); } catch (_) { /* ignore */ } }
        ac = null; masterGain = null;
      }, 350);
      // Release the singleton slot ONLY if this player is still the active
      // one — a newer play() may have already claimed it.
      try {
        if (typeof window !== "undefined" && window.__storyMusicPlayer === player) {
          window.__storyMusicPlayer = null;
        }
      } catch (_) { /* ignore */ }
    },
    setVolume: (v) => {
      if (masterGain) masterGain.gain.value = v;
      if (htmlAudio) htmlAudio.volume = Math.max(0.05, Math.min(1, v * 1.5));
    },
    isMp3: () => !!htmlAudio,
  };

  // Register as the current singleton so stopAnyStoryMusic() and future
  // createStoryMusicPlayer() calls can find & tear it down.
  try { if (typeof window !== "undefined") window.__storyMusicPlayer = player; } catch (_) { /* ignore */ }
  return player;
}

// Public helper: stop any currently-active story-music player app-wide.
// Safe to call from anywhere (e.g. route-change listeners, other pages that
// want to guarantee silence before mounting).
export function stopAnyStoryMusic() {
  try {
    const p = typeof window !== "undefined" ? window.__storyMusicPlayer : null;
    if (p && typeof p.stop === "function") p.stop();
  } catch (_) { /* ignore */ }
  try { if (typeof window !== "undefined") window.__storyMusicPlayer = null; } catch (_) { /* ignore */ }
}

// Belt-and-suspenders: kill any story music if the tab is hidden,
// backgrounded, or the page is being unloaded. This defeats all known
// "music continues outside My Story" edge cases (rapid nav, browser
// back/forward, PWA backgrounding on Android).
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", stopAnyStoryMusic);
  window.addEventListener("beforeunload", stopAnyStoryMusic);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") stopAnyStoryMusic();
  });
}
